/**
 * Notification API Service
 * Provides typed interfaces and API client methods for fetching stored notifications
 * from the backend notification_events table
 */

import { apiClient } from './api';
import { generateNavigationPath } from '../utils/notificationUtils';

export interface StoredNotification {
  id: string;
  event_type: string;
  event_timestamp: string;
  created_at: string;
  data: any;
  is_read: boolean;
  read_at?: string | null;
}

export interface NotificationResponse {
  notifications: StoredNotification[];
  total_count: number;
  returned_count: number;
  has_more: boolean;
  pagination: {
    limit: number;
    offset: number;
    next_offset: number | null;
  };
}

export interface NotificationStats {
  total_count: number;
  days_analyzed: number;
  since: string;
  by_type: Array<{
    event_type: string;
    count: number;
    first_occurrence: string | null;
    last_occurrence: string | null;
  }>;
  by_day: Array<{
    date: string | null;
    count: number;
  }>;
}

export interface UnreadCountResponse {
  unread_count: number;
  days_back: number;
  since: string;
}

/**
 * API client for notification-related endpoints
 */
export const notificationApi = {
  /**
   * Fetch stored notifications with optional filtering and pagination
   */
  getNotifications: async (params?: {
    limit?: number;
    offset?: number;
    unread_only?: boolean;
    event_types?: string[];
    since?: Date;
  }): Promise<NotificationResponse> => {
    const searchParams = new URLSearchParams();
    
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.offset) searchParams.set('offset', params.offset.toString());
    if (params?.unread_only) searchParams.set('unread_only', 'true');
    if (params?.event_types) searchParams.set('event_types', params.event_types.join(','));
    if (params?.since) searchParams.set('since', params.since.toISOString());
    
    console.log(`📡 [Notification API] Fetching notifications with params:`, {
      limit: params?.limit || 50,
      offset: params?.offset || 0,
      event_types: params?.event_types,
      since: params?.since,
      unread_only: params?.unread_only
    });
    
    const response = await apiClient.get(`/notifications?${searchParams}`);
    
    console.log(`✅ [Notification API] Received ${response.data.returned_count} notifications (total: ${response.data.total_count})`);
    
    return response.data;
  },

  /**
   * Mark a specific notification as read (persistent)
   */
  markAsRead: async (notificationId: string): Promise<{ status: string; message: string }> => {
    console.log(`📖 [Notification API] Marking notification ${notificationId} as read`);
    
    const response = await apiClient.post(`/notifications/${notificationId}/mark-read`);
    
    console.log(`✅ [Notification API] Notification ${notificationId} marked as read`);
    return response.data;
  },

  /**
   * Get count of unread notifications within specified timeframe
   */
  getUnreadCount: async (daysBack: number = 7): Promise<UnreadCountResponse> => {
    console.log(`🔢 [Notification API] Getting unread count for last ${daysBack} days`);
    
    const response = await apiClient.get(`/notifications/unread-count?days_back=${daysBack}`);
    
    console.log(`📊 [Notification API] Unread count: ${response.data.unread_count}`);
    
    return response.data;
  },

  /**
   * Get notification statistics for analysis and debugging
   */
  getNotificationStats: async (daysBack: number = 30): Promise<NotificationStats> => {
    console.log(`📈 [Notification API] Getting notification stats for last ${daysBack} days`);
    
    const response = await apiClient.get(`/notifications/stats?days_back=${daysBack}`);
    
    console.log(`📊 [Notification API] Stats: ${response.data.total_count} total notifications across ${response.data.by_type.length} types`);
    
    return response.data;
  },

  /**
   * Mark all notifications as read (persistent)
   */
  markAllAsRead: async (daysBack: number = 7): Promise<{ status: string; message: string; marked_count: number }> => {
    console.log(`📖 [Notification API] Marking all notifications as read (${daysBack} days back)`);
    
    const response = await apiClient.post(`/notifications/mark-all-read?days_back=${daysBack}`);
    
    console.log(`✅ [Notification API] Marked ${response.data.marked_count} notifications as read`);
    return response.data;
  },
};

/**
 * Helper functions for working with stored notifications
 */
export const notificationApiHelpers = {
  /**
   * Convert stored notification to frontend notification format
   */
  convertStoredToFrontend: (stored: StoredNotification): any => {
    // Map event types to frontend notification types
    const typeMapping: Record<string, string> = {
      'open_position_opened': 'position_opened',
      'open_position_closed': 'position_closed',
      'open_position_modified': 'position_modified',
      'open_position_milestone': 'milestone',
      'trade_events': 'trade_closed',
      'unified_notification': 'system',
      'position_milestone': 'milestone', // Direct stored events
      'trading_activity_summary': 'system', // 🔥 NEW: Backend-generated activity summaries
      'session_gap_detected': 'system', // 🔥 NEW: Backend-generated session gap notifications
      'positions_updated': 'system', // 🔥 NEW: Backend-generated position update notifications
      // 🔥 FIX: Add mappings for unified event store event types
      'position_opened': 'position_opened', // Unified event store format
      'position_closed': 'position_closed', // Unified event store format
      'position_modified': 'position_modified', // Unified event store format
      'trade_closed': 'trade_closed', // Unified event store format
    };

    const frontendType = typeMapping[stored.event_type] || 'system';
    
    // 🔥 FIX: Generate click actions for stored notifications
    const generateClickAction = (type: string, data: any) => {
      const symbol = data?.symbol;
      const account = data?.account;
      const tradeId = data?.trade_id;
      
      switch (type) {
        case 'trade_closed':
        case 'open_position_closed':
          if (tradeId && symbol && account) {
            return {
              type: 'navigate',
              payload: {
                path: `/closed-trades/chart/${encodeURIComponent(symbol)}/${encodeURIComponent(account)}/${encodeURIComponent(String(tradeId))}`,
                state: { 
                  from: '/', // Default fallback - will be overridden by current path in handleNotificationClick
                  context: 'stored-notification-click'
                }
              }
            };
          } else if (symbol) {
            return {
              type: 'navigate',
              payload: {
                path: `/closed-trades?filter=${encodeURIComponent(symbol)}`,
                state: { 
                  from: '/', // Default fallback - will be overridden by current path in handleNotificationClick
                  context: 'stored-notification-filter'
                }
              }
            };
          }
          break;
          
        case 'position_opened':
        case 'open_position_opened':
          if (symbol && account) {
            return {
              type: 'navigate',
              payload: {
                path: `/live-trades?filter=${encodeURIComponent(symbol)}&account=${encodeURIComponent(account)}`,
                state: { 
                  from: '/', // Default fallback - will be overridden by current path in handleNotificationClick
                  context: 'stored-notification-live'
                }
              }
            };
          }
          break;
          
        case 'milestone':
        case 'open_position_milestone':
        case 'position_milestone':
          if (symbol && account) {
            // 🔥 FIX: Use proper navigation generation with side parameter for chart view
            // This ensures stored milestone notifications navigate to chart view just like live ones
            
            const navigation = generateNavigationPath({
              positionStatus: 'open', // Milestone notifications are always for open positions
              symbol,
              account,
              side: data?.side, // Use the side parameter from stored data
              tradeId: data?.trade_id
            });
            
            return {
              type: 'navigate',
              payload: {
                path: navigation.fullPath,
                state: { 
                  from: '/', // Default fallback - will be overridden by current path in handleNotificationClick
                  context: 'stored-notification-milestone'
                }
              }
            };
          }
          break;
      }
      
      return null;
    };
    
    return {
      id: `stored_${stored.id}`,
      type: frontendType,
      title: notificationApiHelpers.generateTitle(stored.event_type, stored.data),
      message: notificationApiHelpers.generateMessage(stored.event_type, stored.data),
      timestamp: new Date(stored.event_timestamp).getTime(),
      isRead: stored.is_read,
      source: 'stored', // Track as stored notification
      originalEventId: stored.id, // Link to database event
      clickAction: generateClickAction(frontendType, stored.data), // 🔥 FIX: Add click actions
      metadata: {
        ...stored.data,
        isStoredNotification: true,
        originalId: stored.id,
        eventTimestamp: new Date(stored.event_timestamp).getTime(),
        isRecoveryEvent: true, // All stored notifications are considered recovery events
        // Ensure milestone_percentage is properly converted from string to number for badge display
        milestone_percentage: stored.data.milestone_percentage ? parseFloat(stored.data.milestone_percentage) : stored.data.milestone_percentage,
      }
    };
  },

  /**
   * Generate user-friendly title from stored notification data
   */
  generateTitle: (eventType: string, data: any): string => {
    // Access data directly since it's already the data object from the API
    const symbol = data?.symbol || 'Unknown';
    
    switch (eventType) {
      case 'open_position_opened':
      case 'position_opened':  // Handle unified event store format
        return `Position Opened: ${symbol}`;
      
      case 'open_position_closed':
      case 'position_closed':  // Handle unified event store format
        return `Position Closed: ${symbol}`;
      
      case 'open_position_modified':
      case 'position_modified':  // Handle unified event store format
        return `Position Modified: ${symbol}`;
      
      case 'open_position_milestone':
      case 'position_milestone':
        const milestone = data?.milestone_percentage || 0;
        const milestoneText = milestone === 0 ? 'Break Even' : milestone > 0 ? `${Math.abs(milestone)}% Profit` : `${Math.abs(milestone)}% Loss`;
        return `Milestone Reached: ${symbol} ${milestoneText}`;
      
      case 'trade_closed':
      case 'trade_events':
        // 🔥 IMPROVED: Clearer messaging for closed trades
        return `Closed Trade: ${symbol}`;
      
      case 'unified_notification':
        // For nested events, extract the actual event type
        const nestedEventType = data?.event_type;
        if (nestedEventType && nestedEventType !== 'unified_notification') {
          return notificationApiHelpers.generateTitle(nestedEventType, data);
        }
        return data?.title || 'System Update';
      
      case 'trading_activity_summary':
        return data?.title || 'Trading Summary';
      
      case 'session_gap_detected':
        return data?.title || 'Dashboard Updated';
      
      case 'positions_updated':
        return data?.title || 'Positions Updated';
      
      default:
        return 'System Notification';
    }
  },

  /**
   * Generate user-friendly message from stored notification data
   */
  generateMessage: (eventType: string, data: any): string => {
    // Access data directly since it's already the data object from the API
    const symbol = data?.symbol || 'Unknown';
    const pnl = data?.pnl || data?.final_pnl || 0;
    
    // Note: account and strategy info is now displayed in metadata section with tooltips/marquee
    // so we don't include them in message body for cleaner UX
    
    switch (eventType) {
      case 'open_position_opened':
      case 'position_opened':  // Handle unified event store format
        const side = data?.side || '';
        const size = data?.size || '';
        const leverage = data?.leverage || '';
        const leverageText = leverage ? ` ${leverage}x` : '';
        return `${side} ${size} ${symbol}${leverageText}`;
      
      case 'open_position_closed':
      case 'position_closed':  // Handle unified event store format
        const finalPnl = Math.abs(pnl);
        const outcome = pnl >= 0 ? '🟢 Profit' : '🔴 Loss';
        return `${outcome}: $${finalPnl.toFixed(2)}`;
      
      case 'open_position_modified':
      case 'position_modified':  // Handle unified event store format
        const oldSize = data?.old_size || '';
        const newSize = data?.new_size || '';
        const sizeChange = oldSize && newSize ? ` ${oldSize} → ${newSize}` : '';
        return `Position size changed${sizeChange}`;
      
      case 'open_position_milestone':
      case 'position_milestone':
        // Enhanced milestone formatting to match SSE quality
        const milestone = data?.milestone_percentage || 0;
        const currentPnl = data?.current_pnl || data?.unrealised_pnl || 0;
        const milestroneTrend = data?.milestone_trend || 'improving';
        
        // Generate direction indicators matching SSE format
        const directionIcon = milestroneTrend === 'declining' ? '📉' : '📈';
        const directionText = `(${milestroneTrend})`;
        
        // Generate milestone text matching SSE format
        let milestoneText: string;
        if (milestone === 0) {
          milestoneText = `${directionIcon} Break Even ${directionText}`;
        } else if (milestone > 0) {
          milestoneText = `${directionIcon} ${Math.abs(milestone)}% Profit ${directionText}`;
        } else {
          milestoneText = `${directionIcon} ${Math.abs(milestone)}% Loss ${directionText}`;
        }
        
        const pnlText = currentPnl ? ` (${currentPnl > 0 ? '+' : ''}$${currentPnl.toFixed(2)})` : '';
        return `${data?.symbol} ${milestoneText}${pnlText}`;
      
      case 'trade_closed':
      case 'trade_events':
        const tradePnl = Math.abs(pnl);
        const tradeOutcome = pnl >= 0 ? '🟢 Profit' : '🔴 Loss';
        const qty = data?.qty || '';
        const qtyText = qty ? ` (${qty} units)` : '';
        return `${tradeOutcome}: $${tradePnl.toFixed(2)}${qtyText}`;
      
      case 'unified_notification':
        // For nested events, extract the actual event type
        const nestedEventType = data?.event_type;
        if (nestedEventType && nestedEventType !== 'unified_notification') {
          return notificationApiHelpers.generateMessage(nestedEventType, data);
        }
        return data?.message || 'System notification occurred while you were away';
      
      case 'trading_activity_summary':
        return data?.message || 'Trading activity occurred while you were away';
      
      case 'session_gap_detected':
        return data?.message || 'Your dashboard has been updated with new trading activity while you were away';
      
      case 'positions_updated':
        const count = data?.total_count || 0;
        return data?.message || `${count} position${count === 1 ? '' : 's'} updated`;
      
      default:
        return `System event: ${eventType}`;
    }
  },

  /**
   * Filter stored notifications by type
   */
  filterByTypes: (notifications: StoredNotification[], eventTypes: string[]): StoredNotification[] => {
    return notifications.filter(notification => 
      eventTypes.includes(notification.event_type)
    );
  },

  /**
   * Sort stored notifications by timestamp (newest first)
   */
  sortByTimestamp: (notifications: StoredNotification[]): StoredNotification[] => {
    return [...notifications].sort((a, b) => 
      new Date(b.event_timestamp).getTime() - new Date(a.event_timestamp).getTime()
    );
  },

  /**
   * Group stored notifications by date
   */
  groupByDate: (notifications: StoredNotification[]): Record<string, StoredNotification[]> => {
    const groups: Record<string, StoredNotification[]> = {};
    
    notifications.forEach(notification => {
      const date = new Date(notification.event_timestamp).toDateString();
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(notification);
    });
    
    return groups;
  },
};

/**
 * React Query key factories for notification endpoints
 */
export const notificationApiKeys = {
  all: ['notifications'] as const,
  lists: () => [...notificationApiKeys.all, 'list'] as const,
  list: (filters: Record<string, any>) => [...notificationApiKeys.lists(), filters] as const,
  details: () => [...notificationApiKeys.all, 'detail'] as const,
  detail: (id: string) => [...notificationApiKeys.details(), id] as const,
  stats: (daysBack: number) => [...notificationApiKeys.all, 'stats', daysBack] as const,
  unreadCount: (daysBack: number) => [...notificationApiKeys.all, 'unread-count', daysBack] as const,
};